import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Bath,
  Bed,
  BriefcaseBusiness,
  Building2,
  DoorOpen,
  Sofa,
  Users,
  Utensils,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import {
  roomModeSettingsAtom,
  roomTypeSettingsAtom,
  settingsChangedAtom,
} from '../../atoms/settings';
import {
  ROOM_TYPE_OPTIONS,
  roomTypeOptions,
  type TYPE_ROOM_TYPE_OPTIONS,
} from '../../constants/room-type';
import { SettingsSectionHeader } from './settings-section-header';

const disabledRoomTypeModes = [
  'goodWeather',
  'enhanceAndUpscale',
  'photorealisticFrom3D',
  'cleanRoom',
  'furnitureRemoval',
];

const roomTypeIcons = {
  [ROOM_TYPE_OPTIONS.LIVING_ROOM]: Sofa,
  [ROOM_TYPE_OPTIONS.BEDROOM]: Bed,
  [ROOM_TYPE_OPTIONS.KITCHEN]: Utensils,
  [ROOM_TYPE_OPTIONS.DINING_ROOM]: Utensils,
  [ROOM_TYPE_OPTIONS.KITCHEN_LIVING_COMBO]: Sofa,
  [ROOM_TYPE_OPTIONS.BATHROOM]: Bath,
  [ROOM_TYPE_OPTIONS.HOME_OFFICE]: BriefcaseBusiness,
  [ROOM_TYPE_OPTIONS.OFFICE]: BriefcaseBusiness,
  [ROOM_TYPE_OPTIONS.MEETING_ROOM]: Users,
  [ROOM_TYPE_OPTIONS.COWORKING_SPACE]: Building2,
  [ROOM_TYPE_OPTIONS.KIDS_ROOM]: Bed,
  [ROOM_TYPE_OPTIONS.WALK_IN_CLOSET]: DoorOpen,
};

export const RoomTypeSettings = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const mode = useAtomValue(roomModeSettingsAtom);
  const [roomType, setRoomType] = useAtom(roomTypeSettingsAtom);
  const setSettingsChanged = useSetAtom(settingsChangedAtom);
  const isDisabled = disabledRoomTypeModes.includes(mode);

  useEffect(() => {
    setIsOpen(!isDisabled);
  }, [isDisabled]);

  const getRoomTypeLabel = (value: TYPE_ROOM_TYPE_OPTIONS) => {
    const label = t(value);
    return value === ROOM_TYPE_OPTIONS.KITCHEN_LIVING_COMBO
      ? label.replace('Kitchen Living Combo', 'Kitchen-Living Combo')
      : label;
  };

  const handleSelect = (value: TYPE_ROOM_TYPE_OPTIONS) => {
    if (isDisabled) return;

    setRoomType(value);
    setSettingsChanged(true);
  };

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-sm border border-slate-200 bg-white sm:w-[287px]',
        isDisabled && 'bg-slate-50 text-slate-400'
      )}
    >
      <SettingsSectionHeader
        label={t('roomType')}
        required
        value={!isDisabled && roomType ? getRoomTypeLabel(roomType) : undefined}
        isOpen={isOpen}
        disabled={isDisabled}
        onClick={() => setIsOpen((current) => !current)}
      />

      {isOpen && (
        <div className="grid grid-cols-3 gap-2 px-3 pb-3">
          {roomTypeOptions.map((option) => {
            const Icon = roomTypeIcons[option.value];
            const isSelected = option.value === roomType;

            return (
              <button
                key={option.value}
                type="button"
                disabled={isDisabled}
                aria-label={t(option.value)}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'flex h-[49px] w-full min-w-0 flex-col items-center justify-center gap-1 rounded-sm border px-1.5 text-center text-[9px] font-semibold leading-[11px] transition-colors',
                  isSelected
                    ? 'border-primary-light bg-primary-light/10 text-primary-light ring-1 ring-primary-light'
                    : 'border-slate-200 bg-[#f0f2f580] text-slate-500 hover:border-primary-light',
                  isDisabled &&
                    'cursor-not-allowed bg-slate-100 text-slate-400 hover:border-slate-200'
                )}
              >
                <Icon aria-hidden className="size-4 shrink-0" />
                <span className="w-full whitespace-normal [overflow-wrap:normal] [word-break:normal]">
                  {getRoomTypeLabel(option.value)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
